// src/pages/BankStatement.jsx
import { useState, useRef, useEffect } from 'react'
import { Landmark, Sparkles } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { categorizeTransaction, getAFSJobs, parseBankStatement, getOrCreateDraftJob, saveJobProgress } from '../api/kailux'
import BackButton from '../components/BackButton'


import Icon from '../components/Icon'

import { T, alpha, ink } from '../theme/theme'
const CATEGORIES = [
  { id:'revenue',      label:'Revenue',           color:'#10B981' },
  { id:'cogs',         label:'Cost of Sales',     color:'#F59E0B' },
  { id:'salaries',     label:'Salaries',          color:'#F97316' },
  { id:'paye_uif',     label:'PAYE/UIF/SDL',      color:'#EF4444' },
  { id:'rent',         label:'Rent',              color:'#8B5CF6' },
  { id:'vehicle',      label:'Vehicle/Fuel',      color:'#06B6D4' },
  { id:'insurance',    label:'Insurance',         color:'#EC4899' },
  { id:'bank_charges', label:'Bank Charges',      color:'#6B7280' },
  { id:'utilities',    label:'Utilities',         color:'#84CC16' },
  { id:'comms',        label:'Communication',     color:'#14B8A6' },
  { id:'professional', label:'Professional Fees', color:T.pu },
  // FIX: added after a real bank-statement transaction (a food/restaurant
  // purchase) had no matching category anywhere in this list and the AI
  // categorizer had nothing valid to select either — see
  // kailux-fixes-log.md for the full investigation. Matches the IFRS
  // categories/keywords already used on the trial-balance side
  // (mapping_rules.py) so the two flows stay consistent.
  { id:'entertainment',label:'Entertainment',     color:'#F9A8D4' },
  { id:'repairs',      label:'Repairs & Maintenance', color:'#EAB308' },
  { id:'marketing',    label:'Marketing & Advertising', color:'#0EA5E9' },
  { id:'loan_repay',   label:'Loan Repayments',   color:'#64748B' },
  { id:'interest',     label:'Finance Costs',     color:'#DC2626' },
  { id:'other_exp',    label:'Other Expenses',    color:'#9CA3AF' },
  { id:'other_inc',    label:'Other Income',      color:T.gn },
  // ── Review-queue categories — set by post-processing or accountant ──
  { id:'director_drawing', label:'Director Drawing',  color:'#A855F7' },
  { id:'director_pay',     label:'Director Salary',   color:'#C084FC' },
  { id:'related_entity',   label:'Related Party',     color:'#FB923C' },
  // FEATURE (kailux-fixes-log.md §41 follow-up): resolution target only —
  // never assigned directly by AI categorization or post-processing, only
  // chosen by the accountant via RELATED_OPTIONS. Added here (not to
  // FLAGGED_CATS) purely so catMap[...] resolves a real label/color for the
  // main transactions table instead of undefined once a transaction is
  // resolved to it.
  { id:'related_party_loan', label:'Related Party Loan/Capital', color:'#A855F7' },
  // FEATURE (multi-account support, Sep 2026): a debit in one of the
  // client's own accounts matched against a same-amount credit in another
  // of the client's own accounts within a few days. Never assigned with
  // full confidence by the heuristic alone — it lands in the review queue
  // (FLAGGED_CATS below) for the accountant to confirm or override, same
  // pattern as director_drawing/related_entity. Confirmed transfers are
  // excluded from the P&L entirely (no CAT_TO_IFRS entry below), same
  // treatment class as director_drawing — without this, a transfer between
  // the client's own accounts would otherwise post as both income in one
  // account and an expense in the other, grossing up revenue and expenses
  // on the income statement while leaving net profit only coincidentally
  // correct. This only ever fires when more than one account is present.
  { id:'internal_transfer', label:'Internal Transfer (excl. P&L)', color:'#F472B6' },
  { id:'unclassified',     label:'Needs Review',   color:'#EF4444' },
]

// Transactions with these categories must be reviewed by the accountant
// before the AFS can proceed — they cannot auto-post to any P&L line.
const FLAGGED_CATS = new Set(['director_drawing','director_pay','related_entity','unclassified','internal_transfer'])

const catMap = Object.fromEntries(CATEGORIES.map(c => [c.id, c]))

// Map bank statement categories to IFRS for SMEs figure keys
// These keys must match generate_afs.py exactly
const CAT_TO_IFRS = {
  revenue:      { key:'Revenue',                       side:'credit' },
  cogs:         { key:'Cost of Sales',                 side:'debit'  },
  salaries:     { key:'Employee Costs',                side:'debit'  },
  paye_uif:     { key:'Employee Costs',                side:'debit'  },
  rent:         { key:'Occupancy Costs',               side:'debit'  },
  vehicle:      { key:'Travel and Motor Expenses',     side:'debit'  },
  insurance:    { key:'General and Admin Expenses',    side:'debit'  },
  bank_charges:     { key:'General and Admin Expenses',    side:'debit'  },
  // director_drawing is intentionally absent — it does NOT go to the P&L
  director_pay:     { key:'Employee Costs',                side:'debit'  },
  related_entity:   { key:'Professional Fees',             side:'debit'  },
  utilities:    { key:'Occupancy Costs',               side:'debit'  },
  comms:        { key:'Communication Expenses',        side:'debit'  },
  professional: { key:'Professional Fees',             side:'debit'  },
  entertainment:{ key:'General and Admin Expenses',    side:'debit'  },
  repairs:      { key:'General and Admin Expenses',    side:'debit'  },
  marketing:    { key:'Sales and Marketing',           side:'debit'  },
  interest:     { key:'Finance Costs',                 side:'debit'  },
  // loan_repay and internal_transfer are intentionally absent — see
  // buildFigures() below. Both are genuine cash movements that must NOT
  // hit the P&L (loan_repay is a balance-sheet borrowings movement;
  // internal_transfer is a confirmed transfer between the entity's own
  // linked accounts, not real income/expense). Previously loan_repay's
  // absence here meant it silently vanished from figures with zero signal
  // to the accountant — unlike director_drawing, which was always tracked
  // and surfaced. loan_repay is now tracked the same way director_drawing
  // already is; internal_transfer already had its own equivalent tracking
  // (internalTransferTotal) from the multi-account feature.
  other_exp:    { key:'General and Admin Expenses',    side:'debit'  },
  other_inc:    { key:'Other Income',                  side:'credit' },
}

// Prior year groups — balance sheet only (bank stmt can't give PPE etc)
const PRIOR_GROUPS = [
  {
    label: 'BALANCE SHEET — ASSETS',
    fields: [
      { key:'Property Plant & Equipment',    label:'Property, Plant & Equipment (cost)' },
      { key:'Accumulated Depreciation',      label:'Accumulated Depreciation' },
      { key:'Inventories',                   label:'Inventories' },
      { key:'Trade and Other Receivables',   label:'Trade and Other Receivables' },
      { key:'Tax Receivables',               label:'Tax Receivables' },
      { key:'Cash and Cash Equivalents',     label:'Cash and Cash Equivalents' },
    ]
  },
  {
    label: 'BALANCE SHEET — EQUITY & LIABILITIES',
    fields: [
      { key:'Stated Capital',                label:'Stated Capital / Members\' Contributions' },
      { key:'Retained Earnings',             label:'Retained Earnings' },
      { key:'Loans from Shareholders',       label:'Loans from Shareholders' },
      { key:'Borrowings - Non-Current',      label:'Borrowings — Non-Current' },
      { key:'Borrowings - Current',          label:'Borrowings — Current Portion' },
      { key:'Trade and Other Payables',      label:'Trade and Other Payables' },
      { key:'Tax Payables',                  label:'Income Tax Payable to SARS' },
    ]
  },
  {
    label: 'INCOME STATEMENT (PRIOR YEAR)',
    fields: [
      { key:'Revenue',                       label:'Revenue / Turnover' },
      { key:'Cost of Sales',                 label:'Cost of Sales' },
      { key:'Employee Costs',                label:'Employee Costs' },
      { key:'Occupancy Costs',               label:'Occupancy / Rent' },
      { key:'Finance Costs',                 label:'Finance Costs / Interest' },
      { key:'Income Tax Expense',            label:'Income Tax Expense' },
    ]
  },
]

const EMPTY_PRIOR = Object.fromEntries(
  PRIOR_GROUPS.flatMap(g => g.fields.map(f => [f.key, '']))
)

export default function BankStatement() {
  // NOTE: `location`/`client` must be resolved BEFORE any useState initializer
  // reads them (see below). `location` is a browser global (window.location),
  // so a local `const location = useLocation()` declared further down in this
  // same function scope shadows it for the ENTIRE function body, including
  // code that runs above the declaration — that's how `let`/`const` scoping
  // works in JS. Reading `location` above its own declaration line throws
  // "Cannot access 'location' before initialization" (temporal dead zone),
  // not a silent fallback to window.location. This was previously causing an
  // unconditional crash on every mount, before any effects or user action —
  // caught by ErrorBoundary, which is why the page appeared to "not open" and
  // showed the boundary's fallback screen instead of ever rendering. Keep
  // `location`/`client` declared first, ahead of every hook that reads them.
  const navigate     = useNavigate()
  const location     = useLocation()
  const client       = location.state?.client
  const { firm }     = useAuth()

  // FIX (Sep 2026 — "Back" from the balance-sheet/Review/Statements screens
  // was forcing a full bank-statement re-upload): this page's upload +
  // categorization progress (transactions, their categories, the confirmed
  // period, flagged-item resolutions) has always lived in local useState
  // only, with nothing to restore it on remount. Every route change away
  // from here unmounts this component, so ANY way back — including the
  // explicit `navigate('/bank-statement/...')` calls added on the
  // balance-sheet/Review/Statements pages to fix their own back buttons —
  // landed on a blank "upload" screen, discarding everything already
  // categorized. `BankStatementBalanceSheet.jsx` now round-trips a single
  // `bs_snapshot` object it neither reads nor modifies, all the way through
  // Review.jsx and Statements.jsx and back, purely so this page can restore
  // itself from it here. Same class of bug as the already-fixed "Back to
  // Mapping" wipeout on the trial-balance side (kailux-fixes-log.md §39),
  // just on the bank-statement flow's own first step instead.
  const bs_snapshot = location.state?.bs_snapshot || null

  const [screen,       setScreen]       = useState(bs_snapshot?.screen || 'upload')
  const [transactions, setTransactions] = useState(bs_snapshot?.transactions || [])
  const [cats,         setCats]         = useState(bs_snapshot?.cats || {})
  const [progress,     setProgress]     = useState(0)
  const [current,      setCurrent]      = useState('')
  const [editing,      setEditing]      = useState(null)

  // ── Bank accounts (multi-account support, Sep 2026) ─────────────────────
  // A client can have more than one operating bank account (e.g. a main
  // FNB cheque account plus a Capitec savings account). Each account gets
  // its own upload slot, its own optional statement opening/closing
  // balance (for the reconciliation check below), and every parsed
  // transaction is tagged with the account it came from via account_id —
  // resolved back to a display label via accountLabel() rather than baked
  // into the transaction itself, so renaming an account after upload can't
  // leave stale labels scattered across already-parsed transactions.
  const makeAccountId = () => `acc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,7)}`
  const blankAccount = () => ({
    id: makeAccountId(), label: 'Bank Account',
    openingBalance: '', closingBalance: '',
    uploadedFileName: null, uploading: false, error: '',
  })
  const [accounts, setAccounts] = useState(() => {
    if (bs_snapshot?.accounts?.length) return bs_snapshot.accounts
    return [blankAccount()]
  })
  const [dragOverId, setDragOverId] = useState(null)
  const [reconciliationAcks, setReconciliationAcks] = useState(bs_snapshot?.reconciliationAcks || {})
  const [reconciliationNoteDrafts, setReconciliationNoteDrafts] = useState({})
  const pendingAccountIdRef = useRef(null)

  // A transaction from BEFORE multi-account support has no account_id at
  // all. Treat that as belonging to the client's only account when there
  // is only one (the overwhelmingly common case, and the only shape that
  // existed before this feature) — this keeps every existing in-flight
  // draft and completed job rendering exactly as it did previously.
  const txnAccountId = (t) => t.account_id || (accounts.length === 1 ? accounts[0].id : undefined)
  const accountLabel  = (id) => accounts.find(a => a.id === id)?.label
    || (accounts.length === 1 ? accounts[0].label : 'Bank Account')

  // Prior year state
  const [hasPriorJob,  setHasPriorJob]  = useState(null)
  const [priorFigures, setPriorFigures] = useState(bs_snapshot?.priorFigures || EMPTY_PRIOR)
  const [priorYear,    setPriorYear]    = useState(bs_snapshot?.priorYear || '')
  const [firstYear,    setFirstYear]    = useState(bs_snapshot?.firstYear || false)
  // Restoring via bs_snapshot means the prior-year step was already
  // completed once (it's only ever captured after handlePriorYearDone /
  // handleFirstYearConfirm / handlePeriodConfirmed's first-year branch all
  // set this to true) — re-run the "does this client have a prior job"
  // check regardless, but don't force the prior-year screen back open.
  const [priorChecked, setPriorChecked] = useState(!!bs_snapshot)

  // AFS period — collected before categorisation
  const [financialYear, setFinancialYear] = useState(() => bs_snapshot?.financialYear || String(new Date().getFullYear()))
  const [yearEndDate,   setYearEndDate]   = useState(() => {
    if (bs_snapshot?.yearEndDate) return bs_snapshot.yearEndDate
    const c = client
    return c ? `${c.year_end_day || 28} ${c.year_end_month || 'February'}` : ''
  })

  // Review-queue decisions: idx → resolved category chosen by accountant
  const [flagDecisions, setFlagDecisions] = useState(bs_snapshot?.flagDecisions || {})

  const fileInputRef = useRef(null)

  const totalDebits  = transactions.reduce((s, t) => s + t.debit,  0)
  const totalCredits = transactions.reduce((s, t) => s + t.credit, 0)

  // ── Draft-autosave fix ──────────────────────────────────────────────────
  // Previously this entire pipeline (upload → AI categorization → review
  // queue) held everything in plain React state with no persistence
  // whatsoever until final AFS generation — closing the tab, a network
  // drop, or just navigating away at any point lost the work completely,
  // with nothing written to Supabase and nothing for the Dashboard/AFS
  // History "in progress" lists to show (those just filter afs_jobs by
  // status, and no row existed yet). This mirrors the Trial Balance path's
  // draft-job pattern (getOrCreateDraftJob + saveJobProgress), but autosaves
  // continuously on every field change rather than only at a step boundary
  // — this pipeline never had an existing "confirm and continue" checkpoint
  // the way TB's mapping-confirmation screen does, so there's no single
  // natural moment to autosave at instead.
  const resumedAfsJobIdRef  = useRef(location.state?.afsJobId || null)
  const jobCreationPromiseRef = useRef(null)

  // Rehydrate a resumed session. transactions/cats/flagDecisions don't map
  // onto accounts/mappings the way the trial-balance path's resume data
  // does, so they travel inside the dedicated bank_statement_data blob
  // AFSHistory.jsx / Dashboard.jsx's handleResume now forwards. The actual
  // uploaded File object can't be restored (only its already-parsed output
  // can) — resuming lands past the point where re-upload would be needed.
  useEffect(() => {
    const bd = location.state?.bank_statement_data
    if (!bd?.transactions?.length) return
    setTransactions(bd.transactions)
    setCats(bd.cats || {})
    setFlagDecisions(bd.flagDecisions || {})
    // Multi-account resume: bd.accounts exists on any job saved after this
    // feature shipped. A job saved BEFORE this feature has no `accounts`
    // key at all — synthesize the single implicit account every pre-feature
    // job actually had, using whatever filename was recorded under the old
    // single-file field, so the upload screen still shows something
    // recognisable rather than a bare "Bank Account" placeholder.
    setAccounts(bd.accounts?.length ? bd.accounts : [{
      ...blankAccount(), id: 'acc_default',
      uploadedFileName: bd.uploadedFileName || null,
    }])
    setReconciliationAcks(bd.reconciliation_acks || {})
    setFirstYear(!!bd.first_year)
    setPriorFigures(bd.prior_figures || EMPTY_PRIOR)
    setPriorYear(bd.prior_year || '')
    setPriorChecked(bd.prior_checked !== undefined ? bd.prior_checked : true)
    if (bd.financial_year) setFinancialYear(bd.financial_year)
    if (bd.year_end_date)  setYearEndDate(bd.year_end_date)
    // A resume caught mid-categorization ('processing') can't safely resume
    // that in-flight loop — land on 'review' with whatever categorizations
    // had already been saved instead of silently re-running/duplicating it.
    setScreen(bd.screen === 'processing' ? 'review' : (bd.screen || 'review'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ensureAfsJobId = async () => {
    if (resumedAfsJobIdRef.current) return resumedAfsJobIdRef.current
    if (!client?.id || !firm?.id) return null
    if (!jobCreationPromiseRef.current) {
      jobCreationPromiseRef.current = getOrCreateDraftJob(
        client.id, firm.id, financialYear, undefined, undefined, 'bank_statement'
      ).then((data) => {
        resumedAfsJobIdRef.current = data?.afs_job_id || null
        return resumedAfsJobIdRef.current
      }).catch((err) => {
        console.error('Bank statement draft job creation failed (non-fatal):', err)
        jobCreationPromiseRef.current = null // allow a later retry
        return null
      })
    }
    return jobCreationPromiseRef.current
  }

  // Continuous autosave — fires on every meaningful field change, debounced
  // so a burst of edits (e.g. re-categorizing several transactions in a
  // row) doesn't send a request per keystroke. Best-effort throughout: a
  // save hiccup must never block the preparer from continuing to work, it
  // only means that specific change wouldn't survive a lost session.
  const autosaveTimerRef = useRef(null)
  useEffect(() => {
    if (!client?.id || !firm?.id) return
    if (transactions.length === 0) return // nothing worth persisting yet
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const jobId = await ensureAfsJobId()
        if (!jobId) return
        await saveJobProgress(jobId, {
          stage: 'bank_statement',
          bank_statement_data: {
            screen, transactions, cats, flagDecisions, accounts,
            reconciliation_acks: reconciliationAcks,
            first_year: firstYear, prior_figures: priorFigures, prior_year: priorYear,
            prior_checked: priorChecked,
            financial_year: financialYear, year_end_date: yearEndDate,
          },
        })
      } catch (err) {
        console.error('Bank statement autosave failed (non-fatal):', err)
      }
    }, 600)
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, cats, flagDecisions, accounts, reconciliationAcks, screen, firstYear,
      priorFigures, priorYear, priorChecked, financialYear, yearEndDate, client?.id, firm?.id])

  // Check if client has prior AFS
  useEffect(() => {
    if (!firm?.id || !client?.id) { setPriorChecked(true); return }
    getAFSJobs(firm.id)
      .then(({ data }) => {
        const clientJobs = (data || []).filter(
          j => j.client_id === client.id && j.status === 'complete'
        )
        setHasPriorJob(clientJobs.length > 0)
        // Do NOT auto-skip prior-year screen: bank-statement prior figures
        // must always come from manual entry, not a prior Supabase AFS job.
      })
      .catch(() => { setHasPriorJob(false) })
  }, [firm?.id, client?.id])

  // ── File upload (per account) ───────────────────────────────────────────
  // Re-uploading to an account slot that already has parsed transactions
  // REPLACES only that account's transactions (filtered out by account_id,
  // then the fresh batch appended) — every other account's transactions are
  // untouched. This is only reachable from the 'upload' screen, before
  // categorization has run, so `cats`/`flagDecisions` (keyed by numeric
  // index into `transactions`) are still empty at this point and never go
  // stale from the array being rebuilt here.
  const patchAccount = (id, patch) =>
    setAccounts(prev => prev.map(a => a.id === id ? { ...a, ...patch } : a))

  const handleFileForAccount = async (accountId, file) => {
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['pdf','xlsx','xls','csv'].includes(ext)) {
      patchAccount(accountId, { error: 'Please upload a PDF, Excel (.xlsx) or CSV (.csv) bank statement.' })
      return
    }
    patchAccount(accountId, { uploading: true, error: '', notice: '' })
    try {
      const { data } = await parseBankStatement(file)
      // Tag transactions with the account they came from IN PLACE rather
      // than cloning ({...t, account_id}) — cloning would give each
      // transaction a new object identity, which is harmless in
      // production (transactions round-trip through JSON either way) but
      // breaks anything that tracks a transaction by reference. Mutating
      // the freshly-parsed response objects directly is safe since nothing
      // else holds a reference to them yet.
      const tagged = (data.transactions || []).map(t => { t.account_id = accountId; return t })
      setTransactions(prev => [...prev.filter(t => txnAccountId(t) !== accountId), ...tagged])
      // `notes` (28 Oct 2026): the parser tells the user when it had to correct how it read the file (e.g. it found a
      // running-balance column and worked out money in/out from the balance), and how many rows it could not place.
      patchAccount(accountId, { uploading: false, uploadedFileName: file.name, error: '', notice: data.notes || '' })
    } catch (err) {
      const msg = err?.response?.data?.detail ||
        'Could not read this bank statement. Please check the file and try again.'
      patchAccount(accountId, { uploading: false, uploadedFileName: null, error: msg })
    }
  }

  const handleDropForAccount = (accountId) => (e) => {
    e.preventDefault(); setDragOverId(null)
    const file = e.dataTransfer.files[0]
    if (file) handleFileForAccount(accountId, file)
  }

  const handleInputChange = (e) => {
    const file = e.target.files[0]
    // A direct file-picker interaction with no prior dropzone click can only
    // be unambiguous when there's exactly one account — fall back to it
    // rather than silently dropping the file, matching every pre-multi-
    // account upload interaction.
    const accountId = pendingAccountIdRef.current
      || (accounts.length === 1 ? accounts[0].id : null)
    e.target.value = '' // allow re-selecting the same filename later
    if (file && accountId) handleFileForAccount(accountId, file)
  }

  const addAccount = () => setAccounts(prev => [...prev,
    { ...blankAccount(), label: `Account ${prev.length + 1}` }])

  const removeAccount = (id) => {
    if (accounts.length <= 1) return // always keep at least one account
    setAccounts(prev => prev.filter(a => a.id !== id))
    setTransactions(prev => prev.filter(t => txnAccountId(t) !== id))
  }

  // ── Prior year helpers ────────────────────────────────────────────────────
  const setPriorField = (key, val) =>
    setPriorFigures(prev => ({ ...prev, [key]: val }))

  const priorFiguresForSubmit = () => {
    const out = {}
    Object.entries(priorFigures).forEach(([k, v]) => {
      const n = parseFloat(String(v).replace(/,/g, ''))
      if (!isNaN(n) && n !== 0) out[k] = n
    })
    return out
  }

  // ── Categorization ────────────────────────────────────────────────────────
  const handleCategorizeClick = () => { setScreen('period_confirm') }

  const handlePeriodConfirmed = () => {
    if (firstYear) { setPriorChecked(true); runCategorization(transactions) }
    else { setScreen('prior_year') }
  }

  const handlePriorYearDone = () => {
    setPriorChecked(true)
    runCategorization(transactions)
  }

  const handleFirstYearConfirm = () => {
    setFirstYear(true)
    setPriorFigures(EMPTY_PRIOR)
    setPriorChecked(true)
    runCategorization(transactions)
  }

  const runCategorization = async (txns) => {
    setScreen('processing')
    const results = {}
    for (let i = 0; i < txns.length; i++) {
      const txn = txns[i]
      setCurrent(txn.desc)
      setProgress(Math.round((i / txns.length) * 100))
      try {
        const { data } = await categorizeTransaction(txn)
        results[i] = data
      } catch {
        results[i] = {
          category:     txn.debit > 0 ? 'other_exp' : 'other_inc',
          confidence:   40,
          reasoning:    'Error — review required',
          display_name: 'Review required',
        }
      }
      setCats({ ...results })
    }
    // ── Post-processing: flag transactions needing accountant decision ────────
    // 0. Internal transfers between the client's OWN accounts (only possible
    // — and only checked — when more than one account is in play). A debit
    // in one account matched against a same-amount credit in a different
    // account within a few calendar days is treated as a likely transfer
    // and routed to the review queue rather than auto-posted anywhere: an
    // exact-amount, near-date match across the client's own accounts is a
    // reasonably strong signal, but two unrelated transactions can
    // genuinely share an amount, so this never posts a confirmed final
    // category by itself. Runs BEFORE the director/related-entity checks
    // below and matched indices are skipped by that pass entirely — a
    // transfer's own description sometimes happens to contain a director's
    // name (e.g. "TRANSFER TO J SMITH SAVINGS"), and the amount+date match
    // is the more specific, more reliable signal of the two.
    const transferMatchedIdx = new Set()
    if (accounts.length > 1) {
      const DAY_MS = 24 * 60 * 60 * 1000
      const parseDate = (d) => { const t = Date.parse(d); return isNaN(t) ? null : t }
      for (let i = 0; i < txns.length; i++) {
        if (transferMatchedIdx.has(i) || txns[i].debit <= 0) continue
        const debitAcc  = txnAccountId(txns[i])
        const debitDate = parseDate(txns[i].date)
        for (let j = 0; j < txns.length; j++) {
          if (j === i || transferMatchedIdx.has(j) || txns[j].credit <= 0) continue
          if (txnAccountId(txns[j]) === debitAcc) continue // must be a DIFFERENT account
          if (Math.abs(txns[j].credit - txns[i].debit) > 0.01) continue
          const creditDate = parseDate(txns[j].date)
          if (debitDate != null && creditDate != null &&
              Math.abs(creditDate - debitDate) > 3 * DAY_MS) continue
          transferMatchedIdx.add(i); transferMatchedIdx.add(j)
          results[i] = { category: 'internal_transfer', confidence: 70,
            reasoning: `Matches a same-amount credit in ${accountLabel(txnAccountId(txns[j]))} within a few days — confirm whether this is a transfer between the client's own accounts` }
          results[j] = { category: 'internal_transfer', confidence: 70,
            reasoning: `Matches a same-amount debit in ${accountLabel(debitAcc)} within a few days — confirm whether this is a transfer between the client's own accounts` }
          break
        }
      }
      setCats({ ...results })
    }
    // 1. Director / shareholder name matches
    const rawDirField = client?.director_name || ''
    const dirNames = rawDirField.split(/[\n,]/).map(n => n.trim().toLowerCase()).filter(n => n.length > 2)
    const dirWords = dirNames.flatMap(n => n.split(' ').filter(w => w.length > 2))
    // 2. Related-entity heuristic: significant words from the client's own entity name
    const entityWords = (client?.name || '').toLowerCase()
      .replace(/\b(pty|ltd|inc|cc|npc|rf|the|and|of|for)\b/g, '')
      .split(/\s+/).filter(w => w.length > 3)
    Object.keys(results).forEach(idx => {
      if (transferMatchedIdx.has(parseInt(idx))) return
      const txn      = txns[parseInt(idx)]
      const descLow  = (txn.desc || '').toLowerCase()
      const descTrim = (txn.desc || '').trim()
      // Unidentifiable: very short or no real words
      if (descTrim.length < 4 || !/[a-zA-Z]{2,}/.test(descTrim)) {
        results[idx] = { ...results[idx], category: 'unclassified',
          confidence: 0, reasoning: 'Description too short to classify reliably' }
        return
      }
      // Director name match on debit transactions
      if (txn.debit > 0 && dirWords.some(w => descLow.includes(w))) {
        results[idx] = { ...results[idx], category: 'director_drawing',
          confidence: 90,
          reasoning: 'Payee matches a director name — confirm drawing vs salary vs business expense' }
        return
      }
      // Related entity match (client name substring in payee)
      if (txn.debit > 0 && entityWords.length > 0 && entityWords.some(w => descLow.includes(w))) {
        results[idx] = { ...results[idx], category: 'related_entity',
          confidence: 75,
          reasoning: 'Payee name resembles the client entity — confirm related-party treatment' }
        return
      }
    })
    setCats({ ...results })
    setProgress(100)
    setScreen('review')
  }

  const updateCat = (idx, newCat) => {
    setCats(prev => ({ ...prev, [idx]: { ...prev[idx], category: newCat, confidence: 100 } }))
  }

  // Resolve a flagged transaction — sets both the working category and records the decision
  const resolveFlagged = (idx, chosenCat, label) => {
    setCats(prev => ({ ...prev, [idx]: { ...prev[idx], category: chosenCat, confidence: 100,
      reasoning: `Accountant confirmed: ${label}` } }))
    setFlagDecisions(prev => ({ ...prev, [idx]: chosenCat }))
  }

  // Flagged indices that still need a decision
  const pendingFlags = transactions
    .map((_, i) => i)
    .filter(i => cats[i] && FLAGGED_CATS.has(cats[i].category) && !flagDecisions[i])

  // ── Bank reconciliation (per account) ───────────────────────────────────
  // Optional but strongly encouraged: if the preparer enters the opening
  // and closing balance actually printed on a statement, this checks that
  // figure against the net movement of the transactions extracted FROM
  // that same statement. A mismatch is real evidence of a parsing gap (a
  // row pdfplumber/openpyxl couldn't read, a skipped balance-brought-
  // forward line mistaken for a transaction, etc.) — exactly the kind of
  // silent-completeness failure a CA(SA) compilation engagement can't
  // afford to miss, and one the single-account flow had no way to catch
  // even before multi-account existed. Deliberately NOT used as the AFS's
  // opening cash figure — that stays the prior year's combined Cash and
  // Cash Equivalents balance from the prior-year screen, which is already
  // correct as a single combined number regardless of how many accounts
  // make it up.
  const RECONCILIATION_TOLERANCE = 0.01
  const parseMoney = (v) => {
    const n = parseFloat(String(v ?? '').replace(/,/g, ''))
    return isNaN(n) ? null : n
  }
  const accountStats = accounts.map(acc => {
    const accTxns  = transactions.filter(t => txnAccountId(t) === acc.id)
    const credits  = accTxns.reduce((s, t) => s + t.credit, 0)
    const debits   = accTxns.reduce((s, t) => s + t.debit, 0)
    const netMovement = credits - debits
    const ob = parseMoney(acc.openingBalance)
    const cb = parseMoney(acc.closingBalance)
    const hasBothBalances = ob !== null && cb !== null
    const expectedMovement = hasBothBalances ? (cb - ob) : null
    const diff = hasBothBalances ? (netMovement - expectedMovement) : null
    const mismatched = hasBothBalances && Math.abs(diff) > RECONCILIATION_TOLERANCE
    return { account: acc, txnCount: accTxns.length, credits, debits, netMovement,
      hasBothBalances, expectedMovement, diff, mismatched }
  })
  const pendingReconciliations = accountStats.filter(s => s.mismatched && !reconciliationAcks[s.account.id])
  const acknowledgeReconciliation = (accountId, note) =>
    setReconciliationAcks(prev => ({ ...prev, [accountId]: { note: note || '', ackedAt: new Date().toISOString() } }))

  // Single gate for "Continue to Balance Sheet": both flagged transactions
  // AND unacknowledged reconciliation mismatches must be cleared first.
  const generateBlocked = pendingFlags.length > 0 || pendingReconciliations.length > 0
  const generateBlockedLabel = () => {
    if (pendingFlags.length > 0)
      return `Resolve ${pendingFlags.length} flagged item${pendingFlags.length > 1 ? 's' : ''} first`
    if (pendingReconciliations.length > 0)
      return `Acknowledge ${pendingReconciliations.length} reconciliation mismatch${pendingReconciliations.length > 1 ? 'es' : ''} first`
    return 'Continue to Balance Sheet →'
  }

  // ── Convert categorized transactions → IFRS P&L figures ───────────────────
  // Director drawings are excluded from the P&L entirely — they are a balance
  // sheet movement (loan/drawing), not an expense. They are tracked separately
  // and passed to the balance sheet screen for the accountant to classify.
  // Related-party amounts stay in the P&L but are flagged for Note 18.
  //
  // FEATURE (kailux-fixes-log.md §41 follow-up — "genuine intercompany/
  // related-party loan movements need their own honest treatment, not a
  // reused category"): relatedPartyLoanMovements is tracked the same way as
  // directorDrawings — excluded from the P&L, accumulated separately, and
  // passed to the balance sheet screen — but kept fully distinct from BOTH
  // directorDrawings (so it can never produce the false "payment to the
  // director" statement §41 fixed) AND relatedPartyItems (which stays an
  // actual P&L expense confirmed genuine, unlike a loan/capital movement).
  // Important scope note: this screen's balance sheet fields are entirely
  // manually entered by the accountant (see BankStatementBalanceSheet.jsx —
  // there is no automated bank-statement-to-balance-sheet posting anywhere
  // in this feature, not even for directorDrawings). So this figure is
  // surfaced to the accountant and disclosed narratively in Note 18 for
  // them to reflect in their own manual entry — it does not, and could not
  // consistently with the rest of this feature, auto-populate a balance
  // sheet line itself.
  const buildFigures = () => {
    const figures = {}
    let directorDrawings         = 0
    let directorRemuneration     = 0
    let relatedPartyLoanMovements = 0
    // FIX: loan_repay previously had no entry in CAT_TO_IFRS, so
    // `if (!mapping) return` silently dropped it out of AFS figures
    // entirely — no expense wrongly recorded, but also no signal anywhere
    // that the amount existed at all. Tracked explicitly now, the same way
    // directorDrawings/relatedPartyLoanMovements already are, and forwarded
    // to the balance-sheet screen so the accountant can reconcile against
    // the Borrowings/cash figures they enter manually.
    //
    // NOTE: internal_transfer (below) already covers "a transfer between
    // the client's own linked accounts" via confirmed debit/credit-leg
    // matching (multi-account feature) — no separate keyword-guessed
    // category needed for that concept.
    let loanRepayments      = 0
    let internalTransferTotal    = 0
    const relatedPartyItems  = []
    transactions.forEach((txn, i) => {
      const cat = cats[i]
      if (!cat) return
      // Director drawings never touch the P&L
      if (cat.category === 'director_drawing') { directorDrawings += txn.debit; return }
      // Nor do confirmed related-party loan/capital movements
      if (cat.category === 'related_party_loan') { relatedPartyLoanMovements += txn.debit; return }
      // Nor do loan repayments (a borrowings balance-sheet movement, not
      // an expense)
      if (cat.category === 'loan_repay') { loanRepayments += txn.debit; return }
      // Nor do confirmed transfers between the client's own accounts — see
      // the internal_transfer detection in runCategorization. Only the
      // debit leg is summed here (the matching credit leg in the other
      // account is the same physical movement counted once, not a second
      // one) purely so this total is a meaningful "value moved between
      // accounts" figure rather than double what actually moved.
      if (cat.category === 'internal_transfer') {
        if (txn.debit > 0) internalTransferTotal += txn.debit
        return
      }
      const mapping = CAT_TO_IFRS[cat.category]
      if (!mapping) return
      if (!figures[mapping.key]) figures[mapping.key] = 0
      if (mapping.side === 'credit') {
        figures[mapping.key] += txn.credit
      } else {
        figures[mapping.key] += txn.debit
        if (cat.category === 'director_pay')   directorRemuneration += txn.debit
        if (cat.category === 'related_entity') relatedPartyItems.push({ desc:txn.desc, amount:txn.debit, date:txn.date })
      }
    })
    return { figures, directorDrawings, directorRemuneration, relatedPartyItems,
             relatedPartyLoanMovements, loanRepayments, internalTransferTotal }
  }

  // Opening cash = prior year's closing cash balance (the figure entered on the
  // prior-year screen). Zero for a first-year entity. Closing cash is computed
  // on the next screen as opening + net movement — never net movement alone.
  const getOpeningCash = () => {
    if (firstYear) return 0
    const raw = priorFigures['Cash and Cash Equivalents']
    const n = parseFloat(String(raw).replace(/,/g, ''))
    return isNaN(n) ? 0 : n
  }

  const handleGenerateAFS = () => {
    const { figures: plFigures, directorDrawings, directorRemuneration, relatedPartyItems,
            relatedPartyLoanMovements, loanRepayments, internalTransferTotal } = buildFigures()
    const netCash = totalCredits - totalDebits
    // FIX (Sep 2026 — where should document upload sit in this flow?):
    // moved from after the balance-sheet screen to right after
    // categorization, before it — an uploaded Fixed Asset Register or Loan
    // Agreement can genuinely inform PPE/Borrowings figures for a client
    // with no trial balance to ground them in, so it should feed the
    // balance-sheet screen's pre-fill rather than only enrich note text
    // afterwards (see document_to_afs_figures() on the backend, and
    // AdditionalDocuments.jsx's new figure-conflict handling). Everything
    // this page used to hand straight to BankStatementBalanceSheet.jsx is
    // unchanged below — AdditionalDocuments.jsx doesn't touch any of it,
    // it only adds `figures`/`documentSourcedKeys` on top before
    // forwarding on to /bank-statement/balance-sheet itself.
    navigate('/additional-documents', {
      state: {
        client,
        afsJobId:             resumedAfsJobIdRef.current,
        mappings:             {},
        accounts:             [],
        plFigures,
        directorDrawings,
        directorRemuneration,
        relatedPartyItems,
        relatedPartyLoanMovements,
        loanRepayments,
        internalTransferTotal,
        openingCash:          getOpeningCash(),
        netCash,
        prior_figures:        firstYear ? {} : priorFiguresForSubmit(),
        first_year:           firstYear,
        prior_not_available:  !firstYear && Object.keys(priorFiguresForSubmit() || {}).length === 0,
        // FIX: AdditionalDocuments.jsx reads financial_year/year_end/
        // period_start (snake_case) for its own job-creation logic, while
        // BankStatementBalanceSheet.jsx (the next hop after it) reads
        // financialYear/yearEndDate — sending both conventions means each
        // page finds what it expects, since AdditionalDocuments forwards
        // the raw incoming state through untouched alongside these.
        financial_year:        financialYear,
        year_end:              `${yearEndDate} ${financialYear}`.trim(),
        financialYear,
        yearEndDate:          `${yearEndDate} ${financialYear}`.trim(),
        source:                'bank_statement',
        bank_statement_source: true,
        // FIX (Sep 2026 — "Back" wipeout, see bs_snapshot comment above):
        // opaque round-trip payload so this exact screen (transactions,
        // categorizations, confirmed period) can be restored if the
        // preparer comes back here via any of the downstream "Back"
        // buttons instead of hitting a blank upload screen.
        bs_snapshot: {
          screen: 'review',
          transactions, cats, flagDecisions, accounts, reconciliationAcks,
          priorFigures, priorYear, firstYear,
          financialYear, yearEndDate,
        },
      }
    })
  }

  const R = (n) => n === 0 ? '—' : `R ${Math.abs(n).toLocaleString()}`
  const confColor = (c) => c >= 90 ? T.gn : c >= 70 ? T.yl : T.rd

  // ══════════════════════════════════════════════════════════════════════════
  // UPLOAD SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  const totalExtracted = transactions.length
  const anyUploading = accounts.some(a => a.uploading)
  if (screen === 'upload') return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx }}>

      <input ref={fileInputRef} type="file"
        accept=".pdf,.xlsx,.xls,.csv"
        style={{ display:'none' }}
        onChange={handleInputChange}/>

      {/* Nav */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
        padding:'0 32px', height:'62px', display:'flex', alignItems:'center', gap:'16px' }}>
        <BackButton onClick={() => navigate('/clients')} label="Clients"
          textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800' }}>
          Kai<span style={{color:T.or}}>Lux</span>
        </div>
        {client && <span style={{ fontSize:'14px', color:T.mt }}>/ {client.name} / Bank Statement</span>}
      </div>

      <div style={{ display:'flex', justifyContent:'center', padding:'60px 32px' }}>
        <div style={{ maxWidth:'620px', width:'100%', textAlign:'center' }}>

          <div style={{ fontSize:'12px', letterSpacing:'4px', color:T.or,
            fontWeight:'700', marginBottom:'16px' }}>BANK STATEMENT ENGINE</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'36px',
            fontWeight:'800', letterSpacing:'-1px', marginBottom:'14px' }}>
            Upload {accounts.length > 1 ? '' : 'a '}bank statement{accounts.length > 1 ? 's' : ''}
          </div>
          <div style={{ fontSize:'15px', fontWeight:'400', color:T.lt, lineHeight:'1.7', marginBottom:'32px' }}>
            FNB, ABSA, Nedbank, Standard Bank, Capitec — all supported.
            KaiLux reads your PDF or Excel and categorizes every transaction using AI.
            {' '}Got more than one business bank account? Add each one below —
            KaiLux combines them and checks for transfers between them.
          </div>

          {accounts.map((acc, idx) => (
            <div key={acc.id} style={{ textAlign:'left', marginBottom:'18px',
              border:`1px solid ${T.bd}`, borderRadius:'16px', padding:'20px',
              background:T.s2 }}>

              {/* Account label + remove */}
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                <span style={{ fontSize:'10px', fontWeight:'800', color:T.or,
                  background:T.ord, borderRadius:'6px', padding:'3px 8px', letterSpacing:'0.5px' }}>
                  ACCOUNT {idx + 1}
                </span>
                <input value={acc.label}
                  onChange={e => patchAccount(acc.id, { label: e.target.value })}
                  placeholder="e.g. FNB Cheque Account"
                  style={{ flex:1, padding:'8px 10px', background:T.s3,
                    border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                    fontSize:'13px', fontWeight:'700', fontFamily:"'Outfit',sans-serif",
                    outline:'none' }}/>
                {accounts.length > 1 && (
                  <button onClick={() => removeAccount(acc.id)}
                    title="Remove this account"
                    style={{ padding:'8px 12px', borderRadius:'8px',
                      border:`1px solid ${T.bd}`, background:'transparent',
                      color:T.rd, fontSize:'12px', cursor:'pointer',
                      fontFamily:"'Outfit',sans-serif" }}>
                    Remove
                  </button>
                )}
              </div>

              {acc.error && (
                <div style={{ background:alpha(T.rd, 0.1), border:`1px solid ${T.rd}`,
                  borderRadius:'8px', padding:'10px 14px', marginBottom:'12px',
                  fontSize:'13px', color:T.rd }}>
                  <Icon name="alert" /> {acc.error}
                </div>
              )}
              {acc.notice && !acc.error && (
                <div style={{ background:alpha(T.or, 0.1), border:`1px solid ${T.or}`,
                  borderRadius:'8px', padding:'10px 14px', marginBottom:'12px',
                  fontSize:'13px', color:T.or }}>
                  <Icon name="alert" /> {acc.notice}
                </div>
              )}

              {/* Per-account drop zone */}
              <div
                onClick={() => { if (!acc.uploading) { pendingAccountIdRef.current = acc.id; fileInputRef.current?.click() } }}
                onDrop={handleDropForAccount(acc.id)}
                onDragOver={e => { e.preventDefault(); setDragOverId(acc.id) }}
                onDragLeave={() => setDragOverId(null)}
                style={{ border:`2px dashed ${dragOverId === acc.id ? T.or : acc.uploadedFileName ? T.gn : T.bd}`,
                  borderRadius:'12px', padding:'28px 20px', textAlign:'center',
                  background: dragOverId === acc.id ? T.ord : acc.uploadedFileName ? T.gnd : T.s1,
                  marginBottom:'14px', cursor: acc.uploading ? 'not-allowed' : 'pointer',
                  transition:'all 0.2s' }}>
                {acc.uploading ? (
                  <>
                    <div style={{ fontSize:'26px', marginBottom:'8px' }}><Icon name="hourglass" strokeWidth={1.5} style={{ color: T.mt }} /></div>
                    <div style={{ fontSize:'13px', fontWeight:'700' }}>Reading your bank statement...</div>
                  </>
                ) : acc.uploadedFileName ? (
                  <>
                    <div style={{ fontSize:'26px', marginBottom:'8px' }}><Icon name="check" strokeWidth={1.5} style={{ color: T.gn }} /></div>
                    <div style={{ fontSize:'13px', fontWeight:'700', color:T.gn, marginBottom:'2px' }}>
                      {acc.uploadedFileName}
                    </div>
                    <div style={{ fontSize:'11px', color:T.mt }}>Click to upload a different file for this account</div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom:'8px' }}><Landmark size={24} strokeWidth={1.5} color={T.mt}/></div>
                    <div style={{ fontSize:'13px', fontWeight:'700', marginBottom:'4px' }}>
                      Drop this account's statement here or click to browse
                    </div>
                    <div style={{ fontSize:'11px', color:T.mt }}>PDF, Excel (.xlsx) or CSV</div>
                  </>
                )}
              </div>

              {/* Optional reconciliation inputs */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                <div>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                    color:T.lt, letterSpacing:'0.5px', marginBottom:'5px' }}>
                    STATEMENT OPENING BALANCE (optional)
                  </label>
                  <input type="number" value={acc.openingBalance}
                    onChange={e => patchAccount(acc.id, { openingBalance: e.target.value })}
                    placeholder="0.00"
                    style={{ width:'100%', padding:'8px 10px', background:T.s3,
                      border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                      fontSize:'13px', fontFamily:"'Outfit',sans-serif", outline:'none',
                      boxSizing:'border-box' }}/>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                    color:T.lt, letterSpacing:'0.5px', marginBottom:'5px' }}>
                    STATEMENT CLOSING BALANCE (optional)
                  </label>
                  <input type="number" value={acc.closingBalance}
                    onChange={e => patchAccount(acc.id, { closingBalance: e.target.value })}
                    placeholder="0.00"
                    style={{ width:'100%', padding:'8px 10px', background:T.s3,
                      border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                      fontSize:'13px', fontFamily:"'Outfit',sans-serif", outline:'none',
                      boxSizing:'border-box' }}/>
                </div>
              </div>
              <div style={{ fontSize:'10px', color:T.mt, marginTop:'6px' }}>
                Enter both to get a reconciliation check against this account's own extracted transactions.
              </div>
            </div>
          ))}

          <button onClick={addAccount}
            style={{ width:'100%', padding:'12px', borderRadius:'12px',
              border:`1px dashed ${T.bd}`, background:'transparent', color:T.mt,
              fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
              cursor:'pointer', marginBottom:'20px' }}>
            + Add another bank account
          </button>

          {totalExtracted > 0 && (
            <div style={{ background:T.gnd, border:`1px solid ${alpha(T.gn, 0.2)}`,
              borderRadius:'12px', padding:'16px 20px', display:'flex',
              alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
              <div style={{ textAlign:'left' }}>
                <div style={{ fontSize:'14px', fontWeight:'700', color:T.gn }}>
                  <Icon name="check" /> {totalExtracted} transaction{totalExtracted === 1 ? '' : 's'} extracted
                  {accounts.filter(a => a.uploadedFileName).length > 1
                    ? ` across ${accounts.filter(a => a.uploadedFileName).length} accounts` : ''}
                </div>
                <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                  Ready for AI categorization
                </div>
              </div>
              <button onClick={handleCategorizeClick} disabled={anyUploading}
                style={{ padding:'10px 22px', borderRadius:'8px', border:'none',
                  background: anyUploading ? T.bd : T.prg,
                  color: anyUploading ? T.mt : T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                  fontWeight:'700', cursor: anyUploading ? 'not-allowed' : 'pointer' }}>
                Categorize with AI →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )


  // ══════════════════════════════════════════════════════════════════════════
  // PERIOD CONFIRMATION SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  if (screen === 'period_confirm') return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif", color:T.tx,
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
      <div style={{ position:'fixed', top:0, left:0, right:0, height:'62px', background:T.s1,
        borderBottom:`1px solid ${T.bd}`, display:'flex', alignItems:'center',
        padding:'0 32px', gap:'16px', zIndex:40 }}>
        <BackButton onClick={() => setScreen('upload')} label="Back"
          textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
        <span style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800' }}>
          Kai<span style={{ color:T.or }}>Lux</span></span>
        {client && <span style={{ fontSize:'13px', color:T.mt }}>/ {client.name}</span>}
      </div>
      <div style={{ width:'100%', maxWidth:'520px', padding:'100px 24px 40px' }}>
        <div style={{ fontSize:'11px', letterSpacing:'4px', color:T.or, fontWeight:'700', marginBottom:'10px' }}>
          STEP 1 OF 3 — AFS PERIOD</div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px', fontWeight:'800',
          letterSpacing:'-0.5px', marginBottom:'10px' }}>Confirm the AFS period</div>
        <div style={{ fontSize:'14px', fontWeight:'400', color:T.lt, lineHeight:'1.7', marginBottom:'32px' }}>
          {transactions.length} transactions ready. Confirm the financial year and year-end date
          before KaiLux categorises them — this determines the AFS heading and period.
        </div>
        <div style={{ marginBottom:'20px' }}>
          <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:T.lt,
            letterSpacing:'1px', marginBottom:'8px' }}>FINANCIAL YEAR</label>
          <input type="text" value={financialYear} onChange={e => setFinancialYear(e.target.value)}
            placeholder="e.g. 2026"
            style={{ width:'100%', padding:'12px 14px', background:T.s3, border:`1px solid ${T.bd}`,
              borderRadius:'10px', color:T.tx, fontSize:'15px', fontFamily:"'Outfit',sans-serif",
              outline:'none', boxSizing:'border-box' }}/>
          <div style={{ fontSize:'11px', color:T.mt, marginTop:'5px' }}>
            The year the AFS is issued for — e.g. 2026 for a year ending in 2026.</div>
        </div>
        <div style={{ marginBottom:'20px' }}>
          <label style={{ display:'block', fontSize:'11px', fontWeight:'700', color:T.lt,
            letterSpacing:'1px', marginBottom:'8px' }}>YEAR END DATE (day and month)</label>
          <input type="text" value={yearEndDate} onChange={e => setYearEndDate(e.target.value)}
            placeholder="e.g. 28 February"
            style={{ width:'100%', padding:'12px 14px', background:T.s3, border:`1px solid ${T.bd}`,
              borderRadius:'10px', color:T.tx, fontSize:'15px', fontFamily:"'Outfit',sans-serif",
              outline:'none', boxSizing:'border-box' }}/>
          {yearEndDate && financialYear && (
            <div style={{ fontSize:'11px', color:T.gn, marginTop:'5px' }}>
              AFS will read: For the year ended {yearEndDate} {financialYear}</div>)}
        </div>
        <div style={{ marginBottom:'28px', padding:'16px', background:T.s2,
          border:`1px solid ${T.bd}`, borderRadius:'12px',
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontSize:'13px', fontWeight:'600', color:T.tx, marginBottom:'2px' }}>
              First year of operation</div>
            <div style={{ fontSize:'11px', color:T.mt }}>No comparative figures in the AFS.</div>
          </div>
          <button onClick={() => setFirstYear(p => !p)} style={{ width:'44px', height:'24px',
            borderRadius:'12px', border:'none', background:firstYear ? T.gn : T.bd,
            position:'relative', cursor:'pointer', transition:'background 0.2s', flexShrink:0 }}>
            <span style={{ position:'absolute', top:'3px', width:'18px', height:'18px',
              borderRadius:'50%', background:'white',
              left:firstYear ? '23px' : '3px', transition:'left 0.2s' }}/>
          </button>
        </div>
        <button onClick={handlePeriodConfirmed}
          disabled={!financialYear.trim() || !yearEndDate.trim()}
          style={{ width:'100%', padding:'14px', borderRadius:'10px', border:'none',
            background: financialYear.trim() && yearEndDate.trim()
              ? T.prg : T.bd,
            color: financialYear.trim() && yearEndDate.trim() ? T.onpri : T.mt,
            fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700',
            cursor: financialYear.trim() && yearEndDate.trim() ? 'pointer' : 'not-allowed' }}>
          {firstYear ? 'Confirm — First Year of Operation' : 'Confirm — Enter Prior Year Figures →'}
        </button>
      </div>
    </div>
  )

  // ══════════════════════════════════════════════════════════════════════════
  // PRIOR YEAR SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  if (screen === 'prior_year') return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx }}>

      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
        padding:'0 32px', height:'62px', display:'flex', alignItems:'center',
        justifyContent:'space-between', position:'sticky', top:0, zIndex:40 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <BackButton onClick={() => setScreen('period_confirm')} label="Back"
            textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'17px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          {client && <span style={{ fontSize:'14px', color:T.mt }}>/ {client.name}</span>}
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={handleFirstYearConfirm}
            style={{ padding:'7px 16px', borderRadius:'6px',
              border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
              fontFamily:"'Outfit',sans-serif", fontSize:'13px', cursor:'pointer' }}>
            First year — skip comparatives
          </button>
          <button onClick={handlePriorYearDone}
            style={{ padding:'7px 18px', borderRadius:'6px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'13px',
              fontWeight:'700', cursor:'pointer' }}>
            Save & Continue →
          </button>
        </div>
      </div>

      <div style={{ maxWidth:'700px', margin:'0 auto', padding:'36px 24px' }}>
        <div style={{ marginBottom:'28px' }}>
          <div style={{ fontSize:'12px', letterSpacing:'3px', color:T.or,
            fontWeight:'700', marginBottom:'10px' }}>PRIOR YEAR COMPARATIVE FIGURES</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px',
            fontWeight:'800', letterSpacing:'-0.5px', marginBottom:'10px' }}>
            No prior AFS found for {client?.name}
          </div>
          <div style={{ fontSize:'14px', color:T.mt, lineHeight:'1.7' }}>
            Enter the prior year closing balances from the client's last signed
            financial statements to produce a complete AFS with comparative figures.
          </div>
        </div>

        <div style={{ background:alpha(T.bl, 0.08),
          border:`1px solid ${alpha(T.bl, 0.2)}`, borderRadius:'8px',
          padding:'14px 18px', marginBottom:'28px', fontSize:'14px',
          color:T.bl, lineHeight:'1.6' }}>
          <Icon name="info" /> These figures come from the client's most recent signed financial statements.
          Leave any field blank if not applicable. If this is a brand new entity,
          click <strong>"First year — skip comparatives"</strong> above.
        </div>

        <div style={{ marginBottom:'24px' }}>
          <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
            color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
            PRIOR YEAR ENDED (e.g. 28 February 2024)
          </label>
          <input value={priorYear} onChange={e => setPriorYear(e.target.value)}
            placeholder="e.g. 28 February 2024"
            style={{ width:'280px', padding:'10px 12px', background:T.s3,
              border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
              fontSize:'14px', fontFamily:"'Outfit',sans-serif",
              outline:'none', boxSizing:'border-box' }}
            onFocus={e => e.target.style.borderColor = T.or}
            onBlur={e  => e.target.style.borderColor = T.bd}/>
        </div>

        {PRIOR_GROUPS.map((group, gi) => (
          <div key={gi} style={{ marginBottom:'28px' }}>
            <div style={{ fontSize:'10px', fontWeight:'700', color:T.or,
              letterSpacing:'2px', marginBottom:'14px',
              borderBottom:`1px solid ${T.bd}`, paddingBottom:'8px' }}>
              {group.label}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
              {group.fields.map(field => (
                <div key={field.key}>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'600',
                    color:T.lt, letterSpacing:'0.5px', marginBottom:'4px' }}>
                    {field.label}
                  </label>
                  <div style={{ position:'relative' }}>
                    <span style={{ position:'absolute', left:'10px', top:'50%',
                      transform:'translateY(-50%)', fontSize:'13px', color:T.mt }}>R</span>
                    <input type="number"
                      value={priorFigures[field.key]}
                      onChange={e => setPriorField(field.key, e.target.value)}
                      placeholder="0"
                      style={{ width:'100%', padding:'9px 10px 9px 24px',
                        background:T.s3, border:`1px solid ${T.bd}`,
                        borderRadius:'8px', color:T.tx, fontSize:'14px',
                        fontFamily:"'Outfit',sans-serif",
                        outline:'none', boxSizing:'border-box' }}
                      onFocus={e => e.target.style.borderColor = T.or}
                      onBlur={e  => e.target.style.borderColor = T.bd}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display:'flex', gap:'12px', justifyContent:'flex-end',
          paddingTop:'16px', borderTop:`1px solid ${T.bd}` }}>
          <button onClick={handleFirstYearConfirm}
            style={{ padding:'11px 22px', borderRadius:'8px',
              border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer' }}>
            First year — no comparatives
          </button>
          <button onClick={handlePriorYearDone}
            style={{ padding:'11px 28px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px',
              fontWeight:'700', cursor:'pointer' }}>
            Save & Categorize Transactions →
          </button>
        </div>
      </div>
    </div>
  )

  // ══════════════════════════════════════════════════════════════════════════
  // PROCESSING SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  if (screen === 'processing') return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'40px' }}>
      <div style={{ maxWidth:'500px', width:'100%', textAlign:'center' }}>
        <div style={{ width:'64px', height:'64px',
          background:T.prg,
          borderRadius:'16px', margin:'0 auto 24px', display:'flex',
          alignItems:'center', justifyContent:'center' }}><Sparkles size={26} strokeWidth={1.5} color={T.onpri}/></div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
          fontWeight:'800', marginBottom:'8px' }}>Categorizing Transactions</div>
        <div style={{ fontSize:'14px', color:T.mt, marginBottom:'32px' }}>
          KaiLux is categorizing every transaction
        </div>
        <div style={{ background:T.s1, borderRadius:'999px', height:'8px',
          marginBottom:'12px', overflow:'hidden', border:`1px solid ${T.bd}` }}>
          <div style={{ height:'100%', borderRadius:'999px',
            background:T.or,
            width:`${progress}%`, transition:'width 0.4s' }}/>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between',
          fontSize:'12px', color:T.mt, marginBottom:'20px' }}>
          <span>{Object.keys(cats).length} of {transactions.length} transactions</span>
          <span style={{ color:T.or, fontWeight:'700' }}>{progress}%</span>
        </div>
        <div style={{ background:T.s1, borderRadius:'8px', padding:'12px 16px',
          border:`1px solid ${T.bd}`, fontSize:'12px', color:T.mt,
          fontFamily:"'DM Mono',monospace", textAlign:'left' }}>
          <span style={{ color:T.or }}>▶ </span>{current || 'Initialising...'}
        </div>
      </div>
    </div>
  )

  // ══════════════════════════════════════════════════════════════════════════
  // REVIEW SCREEN
  // ══════════════════════════════════════════════════════════════════════════
  const done = Object.keys(cats).length
  // Extra ACCOUNT column only when it's actually informative (>1 account).
  const tableCols = accounts.length > 1
    ? '90px 1fr 110px 130px 90px 90px 80px'
    : '90px 1fr 140px 90px 90px 80px'
  return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx, paddingBottom:'60px' }}>

      {/* Nav */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
        padding:'0 28px', height:'62px', display:'flex', alignItems:'center',
        justifyContent:'space-between', position:'sticky', top:0, zIndex:40 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'17px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          <span style={{ fontSize:'10px', padding:'2px 10px', borderRadius:'6px',
            background:T.ord, color:T.or, fontWeight:'700' }}>
            Bank Statement Review
          </span>
          {!firstYear && Object.keys(priorFiguresForSubmit()).length > 0 && (
            <span style={{ fontSize:'10px', padding:'2px 10px', borderRadius:'6px',
              background:T.gnd, color:T.gn, fontWeight:'700' }}>
              <Icon name="check" /> Prior year figures loaded
            </span>
          )}
          {firstYear && (
            <span style={{ fontSize:'10px', padding:'2px 10px', borderRadius:'6px',
              background:alpha(T.tx, 0.05), color:T.mt, fontWeight:'700' }}>
              First year — no comparatives
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button
            onClick={() => { setScreen('upload'); setTransactions([]); setCats({});
              setAccounts([blankAccount()]); setReconciliationAcks({}) }}
            style={{ padding:'7px 14px', borderRadius:'6px',
              border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
              fontFamily:"'Outfit',sans-serif", fontSize:'13px', cursor:'pointer' }}>
            ← New Statement
          </button>
          <button onClick={handleGenerateAFS}
            disabled={generateBlocked}
            style={{ padding:'7px 18px', borderRadius:'7px', border:'none',
              background: generateBlocked ? T.bd : T.prg,
              color: generateBlocked ? T.mt : T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'13px',
              fontWeight:'700', cursor: generateBlocked ? 'not-allowed' : 'pointer' }}>
            {generateBlockedLabel()}
          </button>
        </div>
      </div>

      <div style={{ padding:'24px 28px' }}>

        {/* ── BANK RECONCILIATION ────────────────────────────────────────── */}
        {accountStats.some(s => s.hasBothBalances) && (
          <div style={{ marginBottom:'20px', border:`1px solid ${T.bd}`,
            borderRadius:'14px', overflow:'hidden' }}>
            <div style={{ background:T.s1, padding:'14px 18px',
              borderBottom:`1px solid ${T.bd}` }}>
              <div style={{ fontSize:'12px', fontWeight:'800', color:T.tx, letterSpacing:'1px' }}>
                BANK RECONCILIATION</div>
              <div style={{ fontSize:'11px', color:T.mt, marginTop:'3px' }}>
                Extracted transactions checked against each statement's own opening/closing balance.
              </div>
            </div>
            {accountStats.filter(s => s.hasBothBalances).map(s => {
              const draft = reconciliationNoteDrafts[s.account.id] || ''
              const acked = reconciliationAcks[s.account.id]
              return (
                <div key={s.account.id} style={{ padding:'14px 18px',
                  borderBottom:`1px solid ${T.bd}`,
                  background: s.mismatched && !acked ? alpha(T.rd, 0.06) : 'transparent' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                    marginBottom:'6px', flexWrap:'wrap', gap:'8px' }}>
                    <span style={{ fontSize:'13px', fontWeight:'700' }}>{s.account.label}</span>
                    <span style={{ fontSize:'12px', fontWeight:'700',
                      color: !s.mismatched ? T.gn : acked ? T.yl : T.rd }}>
                      {!s.mismatched ? <><Icon name="check" /> Reconciles</>
                        : acked ? <><Icon name="alert" /> Mismatch acknowledged</> : <><Icon name="alert" /> Mismatch</>}
                    </span>
                  </div>
                  <div style={{ fontSize:'11px', color:T.mt, marginBottom:'6px' }}>
                    Statement: R{s.account.openingBalance} → R{s.account.closingBalance}
                    {' '}(expected movement R{s.expectedMovement.toLocaleString()})
                    {' '}· Extracted net movement R{s.netMovement.toLocaleString()} from {s.txnCount} transactions
                    {s.mismatched && ` · Difference R${s.diff.toLocaleString()}`}
                  </div>
                  {s.mismatched && !acked && (
                    <div style={{ display:'flex', gap:'8px', marginTop:'8px', flexWrap:'wrap' }}>
                      <input value={draft}
                        onChange={e => setReconciliationNoteDrafts(prev => ({ ...prev, [s.account.id]: e.target.value }))}
                        placeholder="Note for the file — why this doesn't reconcile (optional)"
                        style={{ flex:1, minWidth:'220px', padding:'8px 10px', background:T.s3,
                          border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                          fontSize:'12px', fontFamily:"'Outfit',sans-serif", outline:'none' }}/>
                      <button onClick={() => acknowledgeReconciliation(s.account.id, draft)}
                        style={{ padding:'8px 16px', borderRadius:'8px', border:'none',
                          background:T.yl, color:T.bg, fontWeight:'700',
                          fontSize:'12px', cursor:'pointer', fontFamily:"'Outfit',sans-serif",
                          whiteSpace:'nowrap' }}>
                        Acknowledge & continue
                      </button>
                    </div>
                  )}
                  {acked?.note && (
                    <div style={{ fontSize:'11px', color:T.lt, marginTop:'6px', fontStyle:'italic' }}>
                      Note: {acked.note}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* ── FLAGGED FOR REVIEW ─────────────────────────────────────────── */}
        {transactions.some((_, i) => cats[i] && FLAGGED_CATS.has(cats[i].category)) && (
          <div style={{ marginBottom:'20px', border:`1px solid ${alpha(T.rd, 0.35)}`,
            borderRadius:'14px', overflow:'hidden' }}>
            <div style={{ background:alpha(T.rd, 0.08), padding:'14px 18px',
              borderBottom:`1px solid ${alpha(T.rd, 0.2)}`,
              display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontSize:'12px', fontWeight:'800', color:T.rd, letterSpacing:'2px' }}>
                  <Icon name="alert" /> REQUIRES YOUR DECISION BEFORE PROCEEDING</div>
                <div style={{ fontSize:'11px', color:T.mt, marginTop:'3px' }}>
                  These transactions cannot auto-post to the AFS — the AI flagged them for accountant review.
                  {pendingFlags.length > 0
                    ? ` ${pendingFlags.length} outstanding.`
                    : <> All resolved <Icon name="check" /></>}
                </div>
              </div>
            </div>
            {transactions.map((txn, i) => {
              const cat = cats[i]
              if (!cat || !FLAGGED_CATS.has(cat.category)) return null
              const resolved = flagDecisions[i]
              const amt = txn.debit > 0 ? `-R ${txn.debit.toLocaleString()}` : `+R ${txn.credit.toLocaleString()}`
              const amtColor = txn.debit > 0 ? T.rd : T.gn

              const DIRECTOR_OPTIONS = [
                { cat:'director_drawing', label:'Director Drawing — balance sheet (not an expense)',
                  hint:'Drawings against a shareholder loan or director loan account. Removed from P&L.',
                  color:'#A855F7' },
                { cat:'director_pay',     label:'Director Salary / Remuneration — Employee Costs',
                  hint:"Arm's length remuneration processed through payroll. Disclosed in Note 18.",
                  color:'#C084FC' },
                { cat:'other_exp',        label:'Business Expense — not director-related',
                  hint:'Ordinary expense that happens to share a name with the director.',
                  color:T.mt },
              ]
              const RELATED_OPTIONS = [
                { id:'stays_related',    cat:'related_entity',   label:'Related Party Service — stays as expense + Note 18',
                  hint:'Expense is genuine but payee is a related entity. Disclosed in Note 18, posted to Professional Fees.',
                  color:T.or },
                { id:'arms_length',      cat:'other_exp',        label:"Arm's-length expense — not a related party",
                  // FIX (kailux-fixes-log.md — "Intercompany Transfer option mislabels
                  // a related-party loan movement as a director drawing"): this hint
                  // claimed the transaction would post to "Professional Fees", but
                  // CAT_TO_IFRS actually routes other_exp to "General and Admin
                  // Expenses" — Professional Fees is where related_entity (the option
                  // above) posts. Corrected to describe what this option actually does.
                  hint:'Confirm the payee is independent. Will post to General and Admin Expenses.',
                  color:T.mt },
                { id:'intercompany',     cat:'related_party_loan',  label:'Related Party — Loan/Capital Movement',
                  // FEATURE (kailux-fixes-log.md §41 follow-up): this used to set
                  // cat:'director_drawing' (fixed to cat:'related_entity' in §41 as
                  // an interim measure), which either falsely attributed the amount
                  // to the director personally, or silently posted it as a
                  // Professional Fees expense despite confirming it isn't a genuine
                  // expense at all. Now uses its own distinct category, tracked
                  // separately from both directorDrawings and relatedPartyItems,
                  // excluded from the P&L, and honestly disclosed in Note 18 as a
                  // related-party loan/capital movement requiring the accountant's
                  // own balance sheet treatment — matching reality: this feature's
                  // balance sheet screen is entirely manual entry (see
                  // BankStatementBalanceSheet.jsx), so nothing in this flow, not
                  // even directorDrawings, auto-posts a balance sheet line. This is
                  // the honest, correctly-scoped treatment, not a lesser one.
                  hint:'Not an expense — removed from the P&L. Disclosed in Note 18 for the accountant to reflect on the balance sheet.',
                  color:'#A855F7' },
              ]
              const TRANSFER_OPTIONS = [
                { cat:'internal_transfer', label:'Confirmed — transfer between the client\'s own accounts',
                  hint:'Excluded from the P&L entirely. Nets to zero across the combined cash position.',
                  color:'#F472B6' },
              ]
              const options = cat.category === 'director_drawing' || cat.category === 'director_pay'
                ? DIRECTOR_OPTIONS
                : cat.category === 'related_entity'
                ? RELATED_OPTIONS
                : cat.category === 'internal_transfer'
                ? TRANSFER_OPTIONS
                : null  // unclassified — show category picker
              // internal_transfer also falls through to the full category
              // picker below (not just its curated option) — unlike
              // director/related-entity matches, a transfer match is a
              // coincidence-prone amount+date heuristic, not a name match,
              // so "actually something else entirely" needs to be a full
              // one-click choice, not a detour through "Business Expense —
              // not director-related"-style options that don't apply here.
              const showFullPickerToo = cat.category === 'internal_transfer'

              return (
                <div key={i} style={{ padding:'16px 18px',
                  borderBottom:`1px solid ${alpha(T.rd, 0.12)}`,
                  background: resolved ? alpha(T.gn, 0.04) : 'transparent' }}>
                  {/* Transaction header */}
                  <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'10px' }}>
                    <span style={{ fontSize:'11px', color:T.mt }}>{txn.date}</span>
                    <span style={{ fontSize:'13px', fontWeight:'600', flex:1 }}>{txn.desc}</span>
                    {accounts.length > 1 && (
                      <span style={{ fontSize:'9px', fontWeight:'700', padding:'2px 7px',
                        borderRadius:'6px', background:alpha(T.tx, 0.06), color:T.lt,
                        whiteSpace:'nowrap' }}>
                        {accountLabel(txnAccountId(txn))}
                      </span>
                    )}
                    <span style={{ fontSize:'13px', fontWeight:'700', color:amtColor }}>{amt}</span>
                    {resolved && (
                      <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px',
                        background:alpha(T.gn, 0.15), color:T.gn, fontWeight:'700' }}>
                        <Icon name="check" /> Resolved
                      </span>
                    )}
                  </div>
                  {/* Reason */}
                  <div style={{ fontSize:'11px', color:T.lt, marginBottom:'10px',
                    padding:'6px 10px', background:alpha(T.rd, 0.06), borderRadius:'6px' }}>
                    {cat.reasoning}
                  </div>
                  {/* Options */}
                  {!resolved && options && (
                    <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                      {options.map(opt => (
                        <button key={opt.id || opt.cat} onClick={() => resolveFlagged(i, opt.cat, opt.label)}
                          style={{ textAlign:'left', padding:'10px 14px', borderRadius:'8px',
                            border:`1px solid ${alpha(opt.color, 0.2)}`,
                            background:`${alpha(opt.color, 0.04)}`,
                            cursor:'pointer', fontFamily:"'Outfit',sans-serif",
                            transition:'all 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = opt.color}
                          onMouseLeave={e => e.currentTarget.style.borderColor = `${alpha(opt.color, 0.2)}`}>
                          <div style={{ fontSize:'12px', fontWeight:'700', color:ink(opt.color), marginBottom:'2px' }}>
                            {opt.label}</div>
                          <div style={{ fontSize:'10px', color:T.mt }}>{opt.hint}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Unclassified (or "not actually a transfer") — full category picker */}
                  {!resolved && (!options || showFullPickerToo) && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop: options ? '8px' : 0 }}>
                      {showFullPickerToo && (
                        <div style={{ width:'100%', fontSize:'10px', color:T.mt, marginBottom:'2px' }}>
                          Not a transfer? Choose the real category:
                        </div>
                      )}
                      {CATEGORIES.filter(c => !FLAGGED_CATS.has(c.id)).map(c => (
                        <button key={c.id} onClick={() => resolveFlagged(i, c.id, c.label)}
                          style={{ padding:'4px 12px', borderRadius:'6px',
                            border:`1px solid ${T.bd}`, cursor:'pointer',
                            fontSize:'10px', fontWeight:'600', background:'transparent',
                            color:T.mt, fontFamily:"'Outfit',sans-serif" }}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Change resolved decision */}
                  {resolved && (
                    <button onClick={() => setFlagDecisions(p => { const n={...p}; delete n[i]; return n })}
                      style={{ fontSize:'10px', color:T.mt, background:'none', border:'none',
                        cursor:'pointer', textDecoration:'underline', fontFamily:"'Outfit',sans-serif" }}>
                      Change decision
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)',
          gap:'12px', marginBottom:'20px' }}>
          {[
            { label:'Total Credits',  value:`R ${totalCredits.toLocaleString()}`, color:T.gn },
            { label:'Total Debits',   value:`R ${totalDebits.toLocaleString()}`,  color:T.rd },
            { label:'Transactions',   value:transactions.length,                  color:T.tx },
            { label:'Categorized',    value:`${done}/${transactions.length}`,      color:T.or },
          ].map((s,i) => (
            <div key={i} style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', padding:'16px' }}>
              <div style={{ fontSize:'9px', color:T.mt, letterSpacing:'1px',
                marginBottom:'6px' }}>{s.label.toUpperCase()}</div>
              <div style={{ fontSize:'20px', fontWeight:'800', color:s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Table header */}
        <div style={{ display:'grid',
          gridTemplateColumns: tableCols,
          padding:'8px 12px', fontSize:'9px', letterSpacing:'1.5px',
          color:T.mt, fontWeight:'700', borderBottom:`1px solid ${T.bd}` }}>
          <div>DATE</div><div>DESCRIPTION</div>
          {accounts.length > 1 && <div>ACCOUNT</div>}
          <div>CATEGORY</div>
          <div style={{textAlign:'right'}}>DEBIT</div>
          <div style={{textAlign:'right'}}>CREDIT</div>
          <div style={{textAlign:'center'}}>CONF</div>
        </div>

        {transactions.map((txn, i) => {
          const cat    = cats[i]
          const catInfo= cat ? catMap[cat.category] : null
          const isEdit = editing === i
          return (
            <div key={i}>
              <div
                onClick={() => !isEdit && setEditing(i)}
                style={{ display:'grid',
                  gridTemplateColumns: tableCols,
                  padding:'9px 12px', borderBottom:`1px solid ${T.bd}`,
                  cursor: isEdit ? 'default' : 'pointer',
                  background: isEdit ? T.ord
                    : i % 2 === 0 ? 'transparent' : alpha(T.tx, 0.01),
                  borderLeft: isEdit ? `3px solid ${T.or}` : '3px solid transparent',
                  alignItems:'center', transition:'background 0.15s' }}
                onMouseEnter={e => { if(!isEdit) e.currentTarget.style.background = T.s2 }}
                onMouseLeave={e => { if(!isEdit) e.currentTarget.style.background =
                  i%2===0?'transparent':alpha(T.tx, 0.01) }}>
                <div style={{ fontSize:'10px', color:T.mt }}>
                  {txn.date}
                </div>
                <div style={{ fontSize:'12px', overflow:'hidden',
                  textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'240px' }}>
                  {txn.desc}
                </div>
                {accounts.length > 1 && (
                  <div style={{ fontSize:'10px', color:T.lt, overflow:'hidden',
                    textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {accountLabel(txnAccountId(txn))}
                  </div>
                )}
                <div>
                  {catInfo && (
                    <span style={{ fontSize:'9px', fontWeight:'700', padding:'2px 7px',
                      borderRadius:'6px', background:`${alpha(catInfo.color, 0.09)}`,
                      color:ink(catInfo.color), whiteSpace:'nowrap' }}>
                      {catInfo.label}
                    </span>
                  )}
                </div>
                <div style={{ textAlign:'right', fontSize:'12px',
                  color: txn.debit > 0 ? T.rd : T.mt }}>
                  {txn.debit > 0 ? R(txn.debit) : '—'}
                </div>
                <div style={{ textAlign:'right', fontSize:'12px',
                  color: txn.credit > 0 ? T.gn : T.mt }}>
                  {txn.credit > 0 ? R(txn.credit) : '—'}
                </div>
                <div style={{ textAlign:'center', fontSize:'12px',
                  fontWeight:'800', color: cat ? confColor(cat.confidence) : T.mt }}>
                  {cat ? `${cat.confidence}%` : '—'}
                </div>
              </div>

              {isEdit && cat && (
                <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
                  padding:'14px 16px', borderLeft:`3px solid ${T.or}` }}>
                  <div style={{ fontSize:'10px', color:T.or, fontWeight:'700',
                    marginBottom:'10px' }}>CHANGE CATEGORY</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                    {CATEGORIES.map(c => (
                      <button key={c.id}
                        onClick={() => { updateCat(i, c.id); setEditing(null) }}
                        style={{ padding:'4px 12px', borderRadius:'6px',
                          border:`1px solid ${cat.category===c.id ? c.color : T.bd}`,
                          cursor:'pointer', fontSize:'10px', fontWeight:'600',
                          background: cat.category===c.id ? `${alpha(c.color, 0.13)}` : 'transparent',
                          color: cat.category===c.id ? ink(c.color) : T.mt,
                          fontFamily:"'Outfit',sans-serif" }}>
                        {c.label}
                      </button>
                    ))}
                    <button onClick={() => setEditing(null)}
                      style={{ padding:'4px 12px', borderRadius:'6px',
                        border:`1px solid ${T.bd}`, background:'transparent',
                        color:T.mt, fontFamily:"'Outfit',sans-serif",
                        fontSize:'10px', cursor:'pointer', marginLeft:'auto' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}

        {/* Bottom CTA */}
        <div style={{ marginTop:'24px', padding:'20px 24px',
          background:T.ord, border:`1px solid ${alpha(T.or, 0.2)}`,
          borderRadius:'12px', display:'flex', justifyContent:'space-between',
          alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <div style={{ fontSize:'15px', fontWeight:'700', marginBottom:'3px' }}>
              {done} transactions categorized
              {!firstYear && Object.keys(priorFiguresForSubmit()).length > 0
                ? ' · Prior year figures included' : ''}
            </div>
            <div style={{ fontSize:'13px', color:T.mt }}>
              Review categories above then generate the AFS
            </div>
          </div>
          <button onClick={handleGenerateAFS}
            disabled={generateBlocked}
            style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
              background: generateBlocked ? T.bd : T.prg,
              color: generateBlocked ? T.mt : T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px',
              fontWeight:'800', cursor: generateBlocked ? 'not-allowed' : 'pointer' }}>
            {generateBlockedLabel()}
          </button>
        </div>
      </div>
    </div>
  )
}